import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const AITimeSeriesChart = ({ title, chartData, chartLabels, yAxisLabel = 'Value' }) => {
  // Validate input data
  const validChartData = Array.isArray(chartData) && chartData.length > 0 ? chartData : [];
  const validLabels = Array.isArray(chartLabels) && chartLabels.length > 0 ? chartLabels : [];
  
  // Check if there's actually data to display
  const hasData = validChartData.length > 0 && validChartData.some(series => 
    Array.isArray(series.data) && series.data.length > 0
  );
  
  // If no data, show empty state
  if (!hasData) {
    return (
      <div className="ai-chart-wrapper">
        <h4 className="ai-chart-title">{title}</h4>
        <div className="ai-chart-empty">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Generate default colors if not provided
  const defaultColors = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6c757d'
  ];

  // Prepare datasets for Chart.js
  const datasets = validChartData.map((series, index) => ({
    label: series.name || `Series ${index + 1}`,
    data: series.data,
    borderColor: series.color || defaultColors[index % defaultColors.length],
    backgroundColor: (series.color || defaultColors[index % defaultColors.length]) + '20',
    borderWidth: 2,
    fill: false,
    tension: 0.1,
    pointRadius: 3,
    pointHoverRadius: 5,
    pointBackgroundColor: series.color || defaultColors[index % defaultColors.length],
    pointBorderColor: '#fff',
    pointBorderWidth: 1,
  }));

  const data = {
    labels: validLabels,
    datasets: datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: validChartData.length > 1,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toFixed(2)} ${yAxisLabel}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11
          },
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11
          }
        },
        title: {
          display: true,
          text: yAxisLabel,
          font: {
            size: 12,
            weight: 'bold'
          }
        }
      }
    }
  };

  return (
    <div className="ai-chart-wrapper">
      <h4 className="ai-chart-title">{title}</h4>
      <div className="ai-chart-container">
        <Line data={data} options={options} />
      </div>
    </div>
  );
};

export default AITimeSeriesChart;
